import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, X, Package, ArrowRight, Loader2 } from 'lucide-react';
import { optimizeImg } from '@/lib/seo';

const API = process.env.REACT_APP_BACKEND_URL;

const HeaderSearch = ({ onClose }) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.length < 2) { setSuggestions([]); setOpen(false); return; }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/api/search/suggest?q=${encodeURIComponent(query)}&limit=6`);
        const data = await res.json();
        setSuggestions(data);
        setOpen(data.length > 0);
      } catch { setSuggestions([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    fetch(`${API}/api/search/track`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ query: query.trim() }) }).catch(() => {});
    setOpen(false);
    onClose?.();
    navigate(`/products?search=${encodeURIComponent(query.trim())}`);
  };

  const handleSuggestionClick = (product) => {
    fetch(`${API}/api/search/track`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ query: query.trim() }) }).catch(() => {});
    setOpen(false);
    onClose?.();
    navigate(`/products/${product.slug || product.id}`);
  };

  return (
    <div className="relative" data-testid="header-search">
      <form onSubmit={handleSubmit} className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search products, grades, standards..."
          className="w-full pl-10 pr-10 py-2.5 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0047AB] focus:border-transparent bg-white"
          data-testid="header-search-input"
        />
        {query && (
          <button type="button" onClick={() => { setQuery(''); setSuggestions([]); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
            <X className="w-4 h-4" />
          </button>
        )}
        {loading && <Loader2 className="absolute right-9 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-[#0047AB]" />}
      </form>

      {/* Suggestions dropdown */}
      {open && suggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-xl z-[100] max-h-80 overflow-y-auto" data-testid="search-suggestions">
          {suggestions.map((p) => (
            <button
              key={p.id}
              onClick={() => handleSuggestionClick(p)}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 text-left border-b border-slate-100 last:border-0 transition-colors"
              data-testid={`suggestion-${p.id}`}
            >
              <div className="w-10 h-10 rounded bg-slate-100 flex-shrink-0 overflow-hidden flex items-center justify-center">
                {p.image_urls?.[0] ? (
                  <img src={optimizeImg(p.image_urls[0])} alt={p.name} className="w-full h-full object-cover" />
                ) : (
                  <Package className="w-5 h-5 text-slate-300" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#0F172A] truncate">{p.name}</p>
                <p className="text-xs text-slate-400">{p.subcategory || p.category} &middot; {p.division}</p>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
            </button>
          ))}
          <button
            onClick={handleSubmit}
            className="w-full px-4 py-3 text-sm text-[#0047AB] font-medium hover:bg-blue-50 text-center transition-colors"
            data-testid="search-view-all"
          >
            View all results for "{query}" <ArrowRight className="w-3.5 h-3.5 inline ml-1" />
          </button>
        </div>
      )}
    </div>
  );
};

export default HeaderSearch;
